import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, UseFormHandleSubmit, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditOrganizationFormValues } from '../../../types/organizationform';
import { getCustomFields } from '../../containers/OrganizationDetailContainer';
import useFormName from '../../hooks/ticketDetailPage/useFormName';
import { usePaginatedOrganizationFieldDefinitionListOnlyStatusParam } from '../../hooks/useOrganizationFieldDefinitionList';
import useOrganizationInfo from '../../hooks/useOrganizationInfo';
import { FieldDefinitionMap, ORGANIZATION_FORM_FIELDS_KEY, UpdateOrganizationPostData } from '../../model/types';
import { updateOrganization } from '../../services/organization/organizationServices';
import { controlledUserFormValue } from '../../utils/forms/formHelper.parser';
import { nonNullishValue } from '../../utils/helpers';
import requestWithMessage from '../../utils/requestWithMessage';
import { getSessionStorageItem } from '../../utils/storageHelpers';
import { hasFieldUserValueType } from './CreateTicketForm';
import { removeUnchangedTicketFieldValues } from './EditTicketForm';
import FormPersistController from './FormPersistController';

interface Props {
    defaultValues: EditOrganizationFormValues;
}

const getEditOrganizationPostDataFromFormValues = (
    defaultValues: EditOrganizationFormValues,
    formValues: EditOrganizationFormValues,
    fieldListMap: FieldDefinitionMap,
    defaultName: string
): UpdateOrganizationPostData => {
    const postDataFields: UpdateOrganizationPostData['fields'] = [];

    // we need to send all active fields, even if they have not changed. "active" word is important because we don't want to update disabled field values
    Object.entries(removeUnchangedTicketFieldValues(formValues, defaultValues)['fields']).forEach(([key, value]) => {
        const postKey = key.replaceAll('$', '.');
        Object.keys(fieldListMap)
            .filter((f) => f === postKey)
            .forEach(() => {
                const postValue =
                    typeof value === 'string'
                        ? hasFieldUserValueType(fieldListMap[postKey])
                            ? controlledUserFormValue(value)
                            : value
                        : value;

                postDataFields.push({
                    key: postKey,
                    value: postValue
                });
            });
    });
    return {
        organizationId: formValues.orgId,
        notes: formValues.notes,
        groupId: formValues.groupId,
        details: formValues.details,
        name: defaultName === formValues.name ? null : formValues.name,
        fields: postDataFields
    };
};

const EditOrganizationForm = ({ children, defaultValues }: PropsWithChildren<Props>) => {
    const { t } = useTranslation('organizations');

    const { mutate: mutateOrganization } = useOrganizationInfo(defaultValues.orgId?.toString());
    const { data: fieldDefinitionList } = usePaginatedOrganizationFieldDefinitionListOnlyStatusParam(true);
    const formName = useFormName();
    const [formHydrated, setFormHydrated] = useState(getSessionStorageItem(formName) ? false : true);
    const methods = useForm<EditOrganizationFormValues>({
        mode: 'onChange',
        defaultValues,
        shouldFocusError: true
    });

    const { handleSubmit, reset, setValue, trigger } = methods;

    useEffect(() => {
        defaultValues && reset(defaultValues);
    }, [defaultValues, reset]);

    useEffect(() => {
        const persistedFormValues = getSessionStorageItem(formName);

        if (persistedFormValues && !formHydrated) {
            Object.entries(persistedFormValues).forEach(([key, value]) => {
                if (nonNullishValue(value) && typeof value === 'object' && !Array.isArray(value)) {
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        if (nonNullishValue(subValue) && typeof subValue === 'object' && !Array.isArray(subValue)) {
                            Object.entries(subValue).forEach(([subSubKey, subSubValue]) => {
                                setValue(`${key}.${subKey}.${subSubKey}` as any, subSubValue, { shouldDirty: true });
                            });
                            return;
                        }

                        setValue(`${key}.${subKey}` as any, subValue, { shouldDirty: true });
                    });
                }
                setValue(key as any, value, { shouldDirty: true });
            });
            setFormHydrated(true);
            trigger();
            return;
        }
    }, [formHydrated, formName, setValue, trigger]);
    const onValid: SubmitHandler<EditOrganizationFormValues> = async (formValues) => {
        const postData = getEditOrganizationPostDataFromFormValues(
            defaultValues,
            formValues,
            fieldDefinitionList.listMap,
            defaultValues?.name
        );
        const startMessage = t('updating');
        const finishMessage = t('updatedSuccesfully');
        const requestName = 'edit-organization';

        // console.log(`EditOrganizationForm, onValid , postData:`, postData);
        await requestWithMessage({
            key: requestName,
            startMessage,
            finishMessage,
            apiRequest: async () => {
                const updatedOrganization = await updateOrganization(postData);
                await mutateOrganization();
                const resetOrganizationValues = {
                    ...updatedOrganization,
                    orgId: updatedOrganization?.id ?? null,
                    name: updatedOrganization?.name ?? null,
                    details: updatedOrganization?.details ?? null,
                    notes: updatedOrganization?.notes ?? null,
                    groupId: updatedOrganization?.group?.id ?? null,
                    [ORGANIZATION_FORM_FIELDS_KEY]: getCustomFields(updatedOrganization)
                };

                Object.keys(defaultValues.fields).forEach((key) => {
                    if (resetOrganizationValues[ORGANIZATION_FORM_FIELDS_KEY][key] === undefined) {
                        resetOrganizationValues[ORGANIZATION_FORM_FIELDS_KEY][key] = null;
                    }
                });

                reset(resetOrganizationValues);
            }
        });
    };

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<EditOrganizationFormValues>>) =>
            (e?: React.BaseSyntheticEvent) =>
                handleOnSubmit(e).catch(() => {
                    // console.error('EditOrganizationForm, onValid, submit error:', e);
                }),
        []
    );

    return (
        <FormProvider {...methods}>
            <FormPersistController formName={formName} />
            <form onSubmit={onSubmit(handleSubmit(onValid))} id="edit-organization-form">
                {children}
            </form>
        </FormProvider>
    );
};

export default EditOrganizationForm;
